/**
 * `/customers/email-notices` (#1911) — operator-authored customer mail.
 *
 * ── WHY THIS IS NOT "MODEL NOTICES" ANY MORE ────────────────────────────────
 * #1877 filed this under the catalog and let one entity decide three things:
 * what the mail said, who received it, and when a model's shelf flipped.
 * Selecting by model is ONE audience policy and "we are delisting X" is ONE
 * piece of copy; neither is what the tool is. So the shelf flip is gone (it
 * lives on the SKU detail page behind a readiness checklist this page never
 * had), the audience is a strategy the operator picks, and the body is their
 * own prose. What is left is a page about putting a message in a paying
 * customer's inbox — which is why it sits next to balance alerts under
 * Customers rather than under upstream supply.
 *
 * The list stays deliberately thin. Everything an operator does to a notice —
 * write it, choose the audience, review who it reaches, prove the channel,
 * send — happens in the drawer, where the steps can be ordered and the gates
 * shown in the order they must be cleared.
 *
 * The stored templates share this page as a second tab. They are the other end
 * of the same job: the copy a notice starts from, and the copy the platform
 * mails on its own.
 */
import { useState } from 'react';
import type { EmailNotice, EmailNoticeStatus } from '@nadoutong/contracts';
import { useEmailNoticeDetail, useEmailNotices } from '../api/useEmailNotices.ts';
import { EmailTemplatesPanel } from '../components/email-notice/EmailTemplatesPanel.tsx';
import { NoticeDrawer } from '../components/email-notice/NoticeDrawer.tsx';
import {
  NOTICE_STATUS_ORDER,
  audienceLabel,
  noticeStatusLabel,
  noticeStatusTone,
} from '../components/email-notice/noticeStatus.ts';
import { useT } from '../i18n/index.ts';
import {
  Button,
  Chip,
  EmptyState,
  PageHeader,
  Panel,
  Skeleton,
  Table,
  TableEmpty,
  Tabs,
  TabsList,
  TabsTrigger,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

const ALL_TAB = 'all';
const NOTICES_VIEW = 'notices';
const TEMPLATES_VIEW = 'templates';

/**
 * Narrow the tab value instead of asserting it. `Tabs` hands back a bare
 * string, and an `as` here would type-launder a typo in a `TabsTrigger` value
 * into a `status=` query the server rejects — the compiler is the only thing
 * that would have caught it.
 */
export function toStatusFilter(tab: string): EmailNoticeStatus | null {
  return NOTICE_STATUS_ORDER.find((status) => status === tab) ?? null;
}

export function EmailNoticesPage(): JSX.Element {
  const t = useT();
  const [view, setView] = useState<string>(NOTICES_VIEW);
  const [tab, setTab] = useState<string>(ALL_TAB);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const statusFilter = toStatusFilter(tab);
  const notices = useEmailNotices(statusFilter);
  const detail = useEmailNoticeDetail(selectedId);

  const openNew = (): void => {
    setSelectedId(null);
    setDrawerOpen(true);
  };

  const openExisting = (notice: EmailNotice): void => {
    setSelectedId(notice.id);
    setDrawerOpen(true);
  };

  const rows = notices.data ?? [];
  const showingTemplates = view === TEMPLATES_VIEW;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('email_notices.title')}
        sub={t('email_notices.subtitle')}
        actions={
          showingTemplates ? null : (
            <Button onClick={openNew} data-testid="email-notices-new">
              {t('email_notices.new')}
            </Button>
          )
        }
        testId="email-notices-header"
      />

      <Tabs value={view} onValueChange={setView}>
        <TabsList ariaLabel={t('email_notices.views_label')}>
          <TabsTrigger value={NOTICES_VIEW} testId="email-notices-view-notices">
            {t('email_notices.view.notices')}
          </TabsTrigger>
          <TabsTrigger value={TEMPLATES_VIEW} testId="email-notices-view-templates">
            {t('email_notices.view.templates')}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {showingTemplates ? (
        <EmailTemplatesPanel />
      ) : (
        <>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList ariaLabel={t('email_notices.tabs_label')}>
              <TabsTrigger value={ALL_TAB} testId="email-notices-tab-all">
                {t('email_notices.tab.all')}
              </TabsTrigger>
              {NOTICE_STATUS_ORDER.map((status) => (
                <TabsTrigger key={status} value={status} testId={`email-notices-tab-${status}`}>
                  {t(noticeStatusLabel(status))}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <Panel caption={t('email_notices.panel.list')} count={rows.length}>
            {notices.isLoading ? (
              <div className="p-4">
                <Skeleton className="h-24 w-full" />
              </div>
            ) : notices.isError ? (
              <div className="p-4">
                <EmptyState
                  title={t('email_notices.error.load')}
                  hint={t('email_notices.error.load_hint')}
                  action={
                    <Button variant="secondary" onClick={() => void notices.refetch()}>
                      {t('common.retry')}
                    </Button>
                  }
                  testId="email-notices-error"
                />
              </div>
            ) : (
              <Table testId="email-notices-table">
                <THead>
                  <Tr interactive={false}>
                    <Th>{t('email_notices.column.task_title')}</Th>
                    <Th>{t('email_notices.column.subject')}</Th>
                    <Th>{t('email_notices.column.audience')}</Th>
                    <Th>{t('email_notices.column.status')}</Th>
                    <Th>{t('email_notices.column.gates')}</Th>
                    <Th>{t('email_notices.column.created')}</Th>
                  </Tr>
                </THead>
                <TBody>
                  {rows.length === 0 ? (
                    <TableEmpty colSpan={6}>{t('email_notices.empty')}</TableEmpty>
                  ) : (
                    rows.map((notice) => (
                      <Tr
                        key={notice.id}
                        onClick={() => openExisting(notice)}
                        data-testid={`email-notices-row-${notice.id}`}
                      >
                        {/* The internal label first, because that is what an
                            operator scans by; the customer-facing subject is
                            beside it rather than instead of it — the whole
                            point of splitting them is that they can differ. */}
                        <Td emphasis>{notice.task_title}</Td>
                        <Td className="text-paper-mid">{notice.subject}</Td>
                        <Td>{t(audienceLabel(notice.audience.type))}</Td>
                        <Td>
                          <Chip
                            tone={noticeStatusTone(notice.status)}
                            testId={`email-notices-status-${notice.id}`}
                          >
                            {t(noticeStatusLabel(notice.status))}
                          </Chip>
                        </Td>
                        <Td>
                          <GateSummary notice={notice} />
                        </Td>
                        <Td mono className="text-paper-low">
                          {notice.created_at.slice(0, 10)}
                        </Td>
                      </Tr>
                    ))
                  )}
                </TBody>
              </Table>
            )}
          </Panel>
        </>
      )}

      <NoticeDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        detail={selectedId === null ? null : detail.data ?? null}
        detailLoading={selectedId !== null && detail.isLoading}
        onCreated={setSelectedId}
      />
    </div>
  );
}

/**
 * Both gates, in the table, in the order they have to be cleared.
 *
 * A notice's most useful property from a list is not its status but how close
 * it is to being sendable, and the status column cannot say that: a `draft`
 * with a frozen list and a proven channel is one press away, and a `draft`
 * created a minute ago is not.
 *
 * Gate one is a verdict here — `recipients_finalized_at` IS the gate. Gate two
 * is not: `ListEmailNoticesResponse` carries `EmailNotice` and nothing else, so
 * a row has no proof rows to judge against, and `test_sent_at` alone is a
 * summary rather than the evidence the gate turns on. The second chip is
 * therefore rendered as a FACT ("a test send is on record", `signal`) and never
 * as a pass (`ok`) — the drawer is where the verdict can actually be computed,
 * and a green row lamp would contradict a blocked send button one click away.
 */
function GateSummary({ notice }: { readonly notice: EmailNotice }): JSX.Element {
  const t = useT();
  return (
    <span className="flex items-center gap-1.5">
      <Chip
        tone={notice.recipients_finalized_at === null ? 'neutral' : 'ok'}
        lamp={false}
        testId={`email-notices-gate-one-${notice.id}`}
      >
        {t('email_notices.gate.one_short')}
      </Chip>
      <Chip
        tone={notice.test_sent_at === null ? 'neutral' : 'signal'}
        lamp={false}
        testId={`email-notices-gate-two-${notice.id}`}
      >
        {notice.test_sent_at === null
          ? t('email_notices.gate.two_short')
          : t('email_notices.gate.two_short_recorded')}
      </Chip>
    </span>
  );
}
